import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../api/authService';
import { useAuth } from '../context/AuthContext';
import '../styles/email-verification.css';

type Status = 'loading' | 'success' | 'expired' | 'invalid';

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#38A16A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const WarnIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#e05252" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#e05252" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const EmailVerificationPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<Status>('loading');
    const [resent, setResent] = useState(false);
    const authContext = useAuth();
    const user = authContext?.user ?? null;
    const navigate = useNavigate();

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('invalid');
            return;
        }
        authService.verifyEmail(token)
            .then(async () => {
                try {
                    const freshUser = await authService.getCurrentUser();
                    authContext?.updateUser(freshUser);
                } catch {
                    // Token expired or not logged in — clear stale auth so the next
                    // page load re-hydrates with email_verified=true after a fresh login
                    localStorage.removeItem('token');
                    localStorage.removeItem('userAuth');
                    authContext?.updateUser(null);
                }
                setStatus('success');
            })
            .catch((err: any) => {
                const httpStatus = err?.response?.status;
                setStatus(httpStatus === 410 ? 'expired' : 'invalid');
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleResend = async () => {
        try {
            await authService.resendVerification();
            setResent(true);
        } catch { /* silent */ }
    };

    return (
        <div className="ev-page">
            <div className="ev-card">
                {status === 'loading' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--loading">
                            <div className="ev-spinner" />
                        </div>
                        <h1 className="ev-title">{t('verify_email.verifying.title')}</h1>
                        <p className="ev-body">{t('verify_email.verifying.body')}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--success"><CheckIcon /></div>
                        <h1 className="ev-title">{t('verify_email.success.title')}</h1>
                        <p className="ev-body">{t('verify_email.success.body')}</p>
                        <div className="ev-actions">
                            <button className="ev-btn" onClick={() => navigate('/practice')}>{t('verify_email.success.go_practice')}</button>
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/dashboard')}>{t('verify_email.success.back_dashboard')}</button>
                        </div>
                    </>
                )}

                {status === 'expired' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><WarnIcon /></div>
                        <h1 className="ev-title">{t('verify_email.expired.title')}</h1>
                        <p className="ev-body">{t('verify_email.expired.body')}</p>
                        <div className="ev-actions">
                            {user && !resent && (
                                <button className="ev-btn" onClick={handleResend}>{t('verify_email.expired.resend')}</button>
                            )}
                            {resent && <p className="ev-body ev-body--muted">{t('verify_email.expired.resent_note')}</p>}
                            {!user && <button className="ev-btn" onClick={() => navigate('/login')}>{t('verify_email.expired.login_to_resend')}</button>}
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/dashboard')}>{t('verify_email.success.back_dashboard')}</button>
                        </div>
                    </>
                )}

                {status === 'invalid' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><XIcon /></div>
                        <h1 className="ev-title">{t('verify_email.invalid.title')}</h1>
                        <p className="ev-body">{t('verify_email.invalid.body')}</p>
                        <div className="ev-actions">
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/dashboard')}>{t('verify_email.success.back_dashboard')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailVerificationPage;
